// Melbourne guide copy, supplied verbatim by the client.
import models from "./models.json";

const pick = (slug, i) => {
  const withPhoto = (models[slug] || []).filter((m) => m.img);
  return withPhoto[i % withPhoto.length]?.img || "";
};

export const siteInfo = {
  name: "Melbourne Brothels Guide",
  tagline: "Melbourne's independent guide to legal adult venues",
  year: "2026",
};

export const guideNav = [
  { id: "introduction", label: "Introduction" },
  { id: "bambra-studio", label: "Bambra Studio" },
  { id: "the-harem-palace", label: "The Harem Palace" },
  { id: "the-pink-palace", label: "The Pink Palace" },
  { id: "history", label: "History" },
  { id: "legal", label: "Is It Legal?" },
  { id: "precincts", label: "Other Establishments" },
  { id: "strip-clubs", label: "Strip Clubs" },
  { id: "what-to-expect", label: "What to Expect" },
  { id: "choosing", label: "Choosing a Venue" },
  { id: "faqs", label: "FAQs" },
  { id: "employment", label: "Working in the Industry" },
  { id: "closing", label: "How Things Have Changed" },
];

export const introHeading =
  "Melbourne Brothels: A Local Guide to the City's Adult Entertainment Scene";

export const introParagraphs = [
  "Melbourne has never been particularly shy about its nightlife. You can find a bar for just about every occasion, restaurants serving dinner well past midnight and clubs that are still going when the sun comes up.",
  "But there's another part of the city's nightlife that people don't tend to talk about quite so openly.",
  "Brothels have been part of Melbourne for generations. Some have become familiar local names, while others have disappeared, changed hands or moved to new premises. And if you've ever tried to find reliable information about them, you'll probably have noticed something. There's plenty of advertising, but finding straightforward advice isn't always easy.",
  "Which places have been around for years? What's the difference between a large South Melbourne brothel and a smaller suburban studio? What happens when you arrive? And are brothels actually legal in Victoria?",
  "There are some genuinely interesting answers to those questions.",
  "We'll look at several well known Melbourne establishments, including Bambra Studio, The Harem Palace and The Pink Palace. We'll also cover the legal changes that have reshaped the industry and a few practical things worth knowing before you visit.",
];

export const historySection = {
  title: "From Little Lon to Modern Melbourne",
  intro: [
    "Long before South Melbourne became known for its adult establishments, much of the action was happening around Little Lonsdale Street.",
    "During the nineteenth century, this pocket of Melbourne was commonly called Little Lon. It had a reputation for drinking, gambling and prostitution, although that reputation tells only part of the story.",
  ],
  blocks: [
    {
      title: "Little Lon and Madame Brussels",
      paragraphs: [
        "The neighbourhood was also full of ordinary families, migrants, shopkeepers and labourers. People lived in cramped cottages and made a living however they could. Some ran perfectly respectable businesses. Others operated in ways that attracted plenty of attention from the authorities.",
        "One of the area's most colourful characters was Caroline Hodgson, better known as Madame Brussels.",
        "Originally from Germany, she became one of Melbourne's best known brothel operators during the 1870s. Her name appeared regularly in newspaper reports, and she became a familiar figure in the city's social history.",
        "Not much of the original Little Lon remains today. Archaeological excavations have uncovered household objects, bottles, ceramics and other belongings that reveal what everyday life was like in the neighbourhood.",
      ],
    },
    {
      title: "Massage Parlours and the Road to Regulation",
      paragraphs: [
        "The adult industry continued to evolve throughout the following century.",
        "By the 1970s, massage parlours had become increasingly visible around Melbourne. Some were legitimate massage businesses, while others operated as brothels under a different description.",
        "It was a difficult situation for the government. The businesses were clearly operating, yet the legal framework had not kept pace with reality.",
        "Victoria began introducing specific brothel regulations in 1984. Further changes followed in 1986, and the Sex Work Act 1994 eventually established a licensing system that remained in place for almost thirty years.",
        "Under that model, operating a brothel involved a substantial amount of regulation.",
        "Some establishments survived for decades. Others closed, relocated or struggled with the cost of compliance.",
        "The next major change came in 2022, and it was a significant one.",
      ],
    },
  ],
};

export const legalSection = {
  title: "Are Brothels Legal in Melbourne?",
  intro:
    "Yes. Consensual adult sex work is legal in Victoria, although there are still rules governing where and how businesses operate.",
  paragraphs: [
    "The major reforms came through the Sex Work Decriminalisation Act 2022.",
    "Rather than changing everything overnight, the Victorian Government introduced the legislation in two stages.",
    "The first began on 10 May 2022. Among other changes, it removed several criminal offences relating to sex work, ended compulsory sexual health testing requirements and introduced stronger protections against discrimination.",
    "The second stage took effect on 1 December 2023.",
    "This was when Victoria abolished the old licensing system for brothels and escort agencies.",
    "Previously, operators needed a special sex work business licence. That requirement no longer exists.",
    "Brothels now operate within the broader rules governing businesses, including relevant planning laws, workplace health and safety requirements and other applicable regulations.",
  ],
  points: [
    "There's an important distinction here. Decriminalisation doesn't mean businesses are free to do whatever they like.",
    "WorkSafe Victoria has responsibilities for workplace safety, local planning rules still matter and laws protecting children and people subjected to coercion remain in force.",
    "The reforms also changed the rules around alcohol.",
    "Brothels can now apply for liquor licences through the normal process, although this doesn't mean every establishment has a bar or serves drinks.",
  ],
  note: "For customers, the legal changes are useful to understand. But the bigger questions are usually much simpler: where should you go, and what should you expect when you get there?",
};

export const venuesIntro = {
  title: "Melbourne Brothels: Three Established Names",
  paragraphs: [
    "There isn't one particular style of brothel that defines Melbourne.",
    "Some businesses occupy large premises with elaborate private rooms. Others are smaller and less conspicuous, particularly in suburban areas.",
    "Three names that regularly come up in discussions about Melbourne's adult industry are Bambra Studio, The Harem Palace and The Pink Palace.",
    "Each has a different history, and that's worth looking at before comparing them.",
  ],
};

export const otherVenuesSection = {
  title: "A Few Other Melbourne Establishments",
  intro: [
    "Bambra, Harem and Pink Palace aren't the only names you'll encounter when looking into Melbourne brothels.",
    "There are other businesses with their own histories and locations, some of which have been discussed by customers for many years.",
  ],
};

export const precincts = [
  {
    title: "The Main Course, West Melbourne",
    text: "Address: 58 Dudley Street, West Melbourne",
    paragraphs: [
      "The Main Course is close to the northern edge of Melbourne's CBD, not far from Flagstaff Gardens and Southern Cross Station.",
      "That makes it a convenient location for people already in the city, including visitors staying around Spencer Street or the western end of the central business district.",
      "The business has appeared in adult industry discussions for years.",
      "For someone looking at city locations, its main practical advantage is accessibility. There's no need to travel across Melbourne simply to make an enquiry or visit the premises.",
      "As with any establishment, check the current arrangements and total price before committing to a booking.",
    ],
  },
  {
    title: "California Club, St Kilda",
    text: "Address: 30 St Kilda Road, St Kilda",
    paragraphs: [
      "St Kilda has always had a slightly different nightlife culture from central Melbourne.",
      "The suburb has a history of music venues, late night bars, entertainment businesses and places that don't fit neatly into the usual tourist descriptions.",
      "California Club is one of the adult establishments associated with the area.",
      "Its position near St Kilda Junction makes it accessible from the city as well as several southern suburbs.",
      "The business has attracted customer discussion over a considerable period, with varying experiences reported.",
      "If you're staying around St Kilda, Windsor or Prahran, it may be a more convenient option than travelling to West Melbourne or Caulfield.",
    ],
  },
  {
    title: "The Boardroom, South Melbourne",
    text: "Address: 53 Market Street, South Melbourne",
    paragraphs: [
      "The Boardroom is another name with a long connection to Melbourne's adult industry.",
      "It's a brothel, despite sometimes being confused with other types of adult entertainment businesses.",
      "In fact, references to The Boardroom appear in Victorian parliamentary discussions from 2001, when the state's brothel regulations were being debated.",
      "That gives some indication of how long the name has been part of the local scene.",
      "Market Street is within easy reach of the CBD, and The Boardroom sits in the wider South Melbourne area that has housed several adult establishments over the years.",
      "It's another business worth distinguishing from the many newer names that appear in online directories.",
    ],
  },
];

export const stripClubsSection = {
  title: "Brothels and Strip Clubs Aren't the Same Thing",
  intro: [
    "This causes more confusion than you might expect, particularly among people visiting Melbourne for the first time.",
    "A brothel provides a setting for privately arranged sexual services between consenting adults.",
    "A strip club, on the other hand, is primarily an entertainment venue. People visit for dancing, stage performances, drinks and the social atmosphere.",
    "The two are often mentioned together because they're part of the wider adult industry, but you shouldn't assume they offer the same services.",
    "Melbourne has several established strip clubs, particularly around King Street.",
  ],
  clubs: [
    {
      title: "The Men's Gallery",
      text: "Address: 601 Lonsdale Street, Melbourne",
      paragraphs: [
        "The Men's Gallery is a familiar name in Melbourne's gentlemen's club scene.",
        "Located near King Street, it's part of a nightlife area that has attracted late evening crowds for decades.",
        "The emphasis is on adult stage entertainment, dancing and a traditional club atmosphere.",
        "For someone planning a night out with friends rather than a private brothel visit, it's a different proposition altogether.",
      ],
    },
    {
      title: "Centrefold Lounge",
      text: "Address: 22 King Street, Melbourne",
      paragraphs: [
        "Centrefold Lounge is another established name within Melbourne's central entertainment district.",
        "The venue is known for its multiple levels and live adult performances.",
        "Being in the King Street precinct makes it convenient for people already spending an evening in the CBD.",
        "If you're deciding between a strip club and a brothel, think about what sort of evening you're actually looking for. They're different businesses with different expectations.",
      ],
    },
  ],
};

export const expectSection = {
  title: "What Actually Happens When You Visit a Brothel?",
  intro:
    "If you've never visited before, it's understandable to have a few questions. The internet is full of dramatic descriptions, but an ordinary visit is generally much less complicated than those stories make it sound. The exact process depends on the business, of course. Not every establishment follows the same routine.",
  steps: [
    {
      title: "First, You'll Usually Speak With Reception",
      paragraphs: [
        "At many brothels, visitors begin by speaking with a receptionist or host.",
        "You can ask who is available, discuss general pricing and find out how the establishment handles introductions.",
        "Some places offer a lounge or waiting area. Others keep things more straightforward.",
        "There's nothing unusual about asking questions, even if it's your first visit.",
        "And you don't have to make a booking simply because you've walked through the door.",
      ],
    },
    {
      title: "Meeting Someone",
      paragraphs: [
        "Depending on the venue, you may have an opportunity to meet available companions before deciding whether to proceed.",
        "This part should be comfortable for both people.",
        "You're entitled to decide whether the arrangement suits you, and the worker is equally entitled to decide whether they wish to accept the booking.",
        "Sometimes there simply isn't a suitable match.",
        "If that happens, there's no need to make it awkward. You can thank reception and leave.",
      ],
    },
    {
      title: "Ask About the Cost Before Paying",
      paragraphs: [
        "Pricing is one area where assumptions can create unnecessary problems.",
        "A quoted amount may cover a particular length of time, while other arrangements can involve separate charges.",
        "Don't assume that every service is included or that all workers offer the same options.",
        "Ask for the total cost and make sure you understand exactly what has been agreed.",
        "It's a simple conversation, but it can prevent an unpleasant surprise later.",
      ],
    },
    {
      title: "Cash, Cards and Discretion",
      paragraphs: [
        "Payment methods differ between businesses.",
        "Some accept electronic transactions, while others have different arrangements.",
        "If you're concerned about privacy, ask how payments are recorded and what information may appear on your statement.",
        "It's better to get a clear answer than assume every transaction is anonymous.",
      ],
    },
    {
      title: "Respecting Personal Boundaries",
      paragraphs: [
        "Once a booking begins, consent remains essential.",
        "People can change their minds, establish limits or decline a particular activity. That applies to everyone involved, regardless of whether money has already changed hands.",
        "A respectful conversation beforehand is far better than discovering halfway through that two people have different expectations.",
      ],
    },
  ],
  etiquetteTitle: "A Little Etiquette Goes a Long Way",
  etiquetteIntro:
    "You don't need to memorise a long list of rules before visiting a Melbourne brothel. Most of the basics are ordinary common sense.",
  etiquette: [
    "Turn up clean. Speak politely to the people working there. Don't assume you're entitled to something that hasn't been discussed.",
    "If you have questions, ask them.",
    "And if someone tells you they're uncomfortable with a request, respect that answer.",
    "Privacy also matters. Taking photos or recording people without their consent is inappropriate and can create serious legal problems.",
  ],
  etiquetteExtra: [
    {
      title: "Alcohol",
      paragraphs: [
        "Another thing to think about is alcohol.",
        "Although Victorian law now allows eligible sex work businesses to obtain liquor licences, that doesn't make arriving heavily intoxicated a good idea.",
        "A venue may refuse service when someone is impaired, and for good reason. Clear judgement and genuine consent matter.",
      ],
    },
    {
      title: "Sexual Health",
      paragraphs: [
        "Sexual health is equally important.",
        "Victoria removed the old mandatory STI testing rules, but that doesn't mean safer sex precautions are unnecessary.",
        "The Department of Health continues to recommend appropriate sexual health measures, including access to condoms, information and voluntary testing.",
        "None of this needs to make a visit feel complicated. It's simply about treating the people involved properly and being sensible about your own health and safety.",
      ],
    },
  ],
};

export const choosingSection = {
  title: "How Do You Decide Which Melbourne Brothel to Visit?",
  intro:
    "There's no shortage of businesses advertising online, and plenty will describe themselves as the best or most luxurious in Melbourne. Those claims don't tell you very much.",
  points: [
    "I'd start with the location. If you're staying around Southbank, a South Melbourne establishment may be convenient. If you live in Caulfield, the attraction of travelling across town might be fairly limited when there's a local option.",
    "Then consider the kind of place you'd feel comfortable visiting. A large establishment with elaborate interiors can appeal to someone who enjoys that setting. Another person might be happier with a smaller studio that concentrates on a simpler booking experience. Neither is automatically better.",
    "Price is another factor, especially if you're comparing several venues. Make sure you're comparing the full cost of a booking rather than two figures that cover different things.",
  ],
  safetyTitle: "Reading Reviews Properly",
  safety: [
    "And then there are reviews.",
    "Independent customer discussions can be helpful, but they need a little interpretation.",
    "A glowing review written eight years ago doesn't prove much about an establishment today. Staff change, management changes and sometimes the entire business moves to another address.",
    "Recent feedback is more useful, particularly when several people mention the same issues.",
    "It's also worth looking beyond star ratings. A detailed comment about communication, reception or pricing often tells you more than a five star review containing only a few words.",
  ],
  safetyNote:
    "Finally, don't underestimate the usefulness of a phone call. You can ask about availability, opening hours and general arrangements before travelling. For a first visit, that can make things considerably easier.",
  paymentTitle: "Before You Travel",
  payment: [
    "Check the location against where you're actually staying.",
    "Decide whether you prefer a larger establishment or a smaller studio.",
    "Compare the full cost of a booking, not two figures covering different things.",
    "Give weight to recent feedback rather than years-old reputation.",
  ],
};

export const faqSection = {
  title: "Frequently Asked Questions About Melbourne Brothels",
  items: [
    {
      q: "Can I legally visit a brothel in Melbourne?",
      a: [
        "Yes. Consensual sexual services between adults are legal in Victoria, subject to applicable laws and regulations.",
        "The state's decriminalisation reforms were introduced in May 2022 and December 2023.",
      ],
    },
    {
      q: "Do brothels still need a government licence?",
      a: [
        "Not the special sex work business licence that existed under the old system.",
        "That requirement ended on 1 December 2023. Businesses must still comply with relevant planning, safety and other legal obligations.",
      ],
    },
    {
      q: "Can I arrive without an appointment?",
      a: [
        "Some establishments accept visitors who haven't booked. Others may prefer people to call ahead.",
        "If you have a particular time in mind, it's sensible to check first.",
      ],
    },
    {
      q: "Are Melbourne brothels open 24 hours?",
      a: [
        "Not necessarily.",
        "Opening hours vary from one business to another. Some focus on daytime appointments, while others operate into the evening or early morning.",
        "Always confirm current hours rather than relying on an old directory listing.",
      ],
    },
    {
      q: "Can couples visit a Melbourne brothel?",
      a: [
        "Certain establishments may accommodate couples, but availability and policies vary.",
        "Contact the business before visiting to discuss whether it can accommodate what you're looking for.",
      ],
    },
    {
      q: "Will my visit be confidential?",
      a: [
        "Discretion is an important consideration for many customers, but privacy policies are not identical everywhere.",
        "If you have concerns about booking records, personal information or card transactions, ask the venue how those matters are handled.",
      ],
    },
    {
      q: "Do sex workers still have to undergo compulsory health testing?",
      a: [
        "No. Victoria removed the former mandatory STI testing requirements as part of the 2022 reforms.",
        "Voluntary testing, sexual health education and safer sex practices remain important.",
      ],
    },
    {
      q: "How much does it cost to visit a Melbourne brothel?",
      a: [
        "There isn't one standard price across the industry.",
        "Costs depend on the business, booking duration, worker and agreed arrangements. Ask for an accurate quote before paying.",
      ],
    },
    {
      q: "Is it safer to choose an established brothel?",
      a: [
        "A long operating history can provide useful background, but it isn't a guarantee of safety or a good experience.",
        "Current practices, clear communication, respect for consent and appropriate workplace safety measures matter much more than how long a business name has been around.",
      ],
    },
  ],
};

export const employmentSection = {
  title: "Working in Melbourne's Adult Industry",
  paragraphs: [
    "Victoria's legal reforms didn't just affect customers. They also changed the environment for people working in the industry.",
    "The removal of the old licensing framework means sex workers now operate under broader workplace laws, with protections relating to discrimination, occupational health and safety and other employment matters.",
    "That doesn't mean every arrangement is identical.",
    "Someone considering working at a brothel should understand how the business operates, whether room fees apply, what contractual arrangements are proposed and what procedures are in place for personal safety.",
    "It's also worth seeking independent advice before accepting an unfamiliar arrangement.",
    "Victoria's peer sex worker organisation, Vixen, provides information, advocacy and support for people working in the industry.",
    "For those exploring opportunities, our Melbourne adult jobs page is another place to begin looking.",
  ],
};

export const closingSection = {
  title: "Melbourne's Adult Industry Has Changed, but Some Names Remain",
  paragraphs: [
    "If you compare Melbourne's adult industry today with what existed thirty or forty years ago, the differences are substantial.",
    "The legal framework has changed. Businesses have moved or closed. Some familiar names have survived while others belong entirely to the city's past.",
    "Yet there is still a recognisable connection between the older establishments and the businesses operating in Melbourne today.",
    "Bambra's connection with Georgie's Place, Harem's longstanding association with Park Street and the history of The Pink Palace are all examples.",
    "That's part of what makes researching these establishments interesting. You're not simply comparing advertisements. You're also looking at businesses that have operated through years of changing laws and attitudes.",
    "If you're thinking about visiting, there's no need to overcomplicate the decision.",
    "Find somewhere convenient, make sure the business suits what you're looking for and ask about pricing and availability before you go.",
    "Read current reviews rather than relying entirely on old reputations. And treat everyone you meet with the same consideration you'd expect yourself.",
    "A bit of common sense and a few minutes of research will generally tell you far more than any flashy advertising claim.",
  ],
};

export const guideImages = {
  red: pick("the-pink-palace", 1),
  green: pick("the-harem-palace", 2),
  black: pick("bambra-studio", 1),
  pink: pick("the-pink-palace", 4),
};
